import {ActionReference, GameObjectReference, GameState} from "@shared/types";
import {css, html, LitElement, nothing, TemplateResult} from "lit";
import {customElement} from "lit/decorators.js";
import {getState, performAction} from "../services/routeService";
import {currentRoom} from "@adventure-game/api/src/currentRoom";

@customElement("game-canvas")
export class GameCanvas extends LitElement {
    public static styles = css`
        .game {
            height: 100%;
            display: grid;
            grid-template-columns: 1fr;
            grid-template-rows: auto auto 1fr auto;
            grid-column-gap: 0;
            grid-row-gap: 0;
            user-select: none;
        }

        .title {
            text-align: center;
            flex-grow: 1;
            font-size: 30px;
            user-select: text;
            text-transform: uppercase;
            font-weight: bold;
        }

        .header {
            display: flex;
            flex-direction: column;
            align-items: center;
            flex-grow: 1;
            position: relative;
            margin-top: 2px;
        }

        .header img {
            width: 100%;
            height: auto;
            image-rendering: pixelated;
        }

        .header img:nth-child(n + 2) {
            position: absolute;
        }

        .content {
            flex-grow: 1;
            overflow: auto;
            margin-top: 10px;
            padding: 0 10px;
            user-select: text;
        }

        .content p {
            margin: 0 0 10px 0;
        }

        .content p:last-of-type {
            margin: 0;
        }

        .footer {
            background-color: #313638;
            border: 1px solid #131010;
            margin-top: 10px;
            display: flex;
            height: 130px;
        }

        .footer .buttons {
            display: flex;
            flex-direction: column;
            overflow: auto;
            padding: 10px 10px 0 10px;
            width: 100%;
        }

        .footer .button {
            background-color: #313638;
            border: 1px solid #131010;
            padding: 2px 10px;
            margin: 0 0 5px 10px;
            text-transform: uppercase;
            cursor: pointer;
            display: inline-block;
            user-select: none;
        }

        .footer .button.active,
        .footer .button:hover {
            background-color: #131010;
        }

        .footer .inventory {
            display: flex;
            flex-direction: column;
            padding: 10px 10px 0 10px;
            border-left: 1px solid #131010;
            width: 50%;
            height: 100px;
            margin: 0 auto;
            overflow: hidden;
        }

        .footer .inventory h2 {
            font-size: 25px;
            font-weight: normal;
            margin: 0 0 4px 0;
            background-color: #313638;
            border: 1px solid #131010;
            text-transform: uppercase;
            text-align: center;
        }

        .footer .inventory .inventory-item {
            background-color: #313638;
            border: 1px solid #131010;
            padding: 2px 10px;
            margin: 0 0 5px 0;
            text-transform: uppercase;
            display: inline-block;
        }

        .footer .inventory:hover {
            overflow-y: scroll;
        }

        .topBar {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin: 10px 0 0 0;
        }

        .topBar img {
            width: 60px;
            height: 60px;
            cursor: pointer;
        }
    `;

    public connectedCallback(): void {
        super.connectedCallback();

        void this.refreshState();
    }

    protected render(): TemplateResult {
        return html`
            <div class="game">
                ${this.renderTitle()} ${this.renderHeader()} ${this.renderContent()} ${this.renderFooter()}
            </div>
        `;
    }

    public contentText?: string[];
    public inventoryItems?: string[];
    private roomTitle?: string;
    private roomImages?: string[];
    private actionButtons?: ActionReference[];
    private gameObjectButtons?: GameObjectReference[];
    private selectedActionButton?: ActionReference;
    private selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    public handleClickHint(): void {
        const customElement: Element = document.querySelector("hint-component") as Element;
        const shadowRoot: ShadowRoot = customElement.shadowRoot as ShadowRoot;
        const hint: HTMLElement = shadowRoot.querySelector(".hint-container") as HTMLElement;

        hint.style.display = "flex";

        this.requestUpdate();
    }

    private async refreshState(): Promise<void> {
        const state: GameState = await getState();

        this.updateState(state);
    }

    private updateState(state: GameState): void {
        //Reset the component
        this.roomTitle = state.roomTitle;
        this.roomImages = state.roomImages;
        this.contentText = state.text;
        this.actionButtons = state.actions;
        this.gameObjectButtons = state.objects;
        this.inventoryItems = state.inventory;

        this.selectedActionButton = undefined;
        this.selectedGameObjectButtons.clear();

        currentRoom.currentRoom = state.roomAlias;

        this.requestUpdate();
    }

    private async handleClickAction(button: ActionReference): Promise<void> {
        if (button.needsObject) {
            this.selectedActionButton = button;
            this.selectedGameObjectButtons.clear();

            this.requestUpdate();
        } else {
            const state: any = await performAction(button.alias);

            if (state === undefined) {
                return;
            }

            this.updateState(state);
        }
    }

    private async handleClickObject(button: GameObjectReference): Promise<void> {
        if (!this.selectedActionButton) {
            return;
        }

        this.selectedGameObjectButtons.add(button);

        const state: GameState | undefined = await performAction(
            this.selectedActionButton.alias,
            [...this.selectedGameObjectButtons].map((e) => e.alias)
        );

        if (this.selectedGameObjectButtons.size >= 2) {
            this.selectedActionButton = undefined;
            this.selectedGameObjectButtons.clear();
        }

        this.requestUpdate();

        if (state === undefined) {
            return;
        }

        this.updateState(state);
    }

    private handleClickSettings(): void {
        if (confirm("Are you sure you want to leave the game?")) {
            window.location.href = "index.html";
        }
    }

    private handleClickRestart(): void {
            window.location.href = "index.html";
    }

    private handleClickMap(): void {
        alert("Map feature wasn't developed...");
    }

    private renderTitle(): TemplateResult {
        if (this.roomTitle) {
            return html`
                <div class="topBar">
                    <img src="/assets/img/items/Hint.png" alt="hint" class="hint"
                         @click="${this.handleClickHint}">
                    <div class="title">${this.roomTitle}</div>
                    <img src="/assets/img/items/MapPin.png" alt="map" class="map"
                         @click="${this.handleClickMap}">
                    <img src="/assets/img/items/Settings.png" alt="settings" class="settings"
                         @click="${this.handleClickSettings}">
                </div>`;
        }

        return html`${nothing}`;
    }

    private renderHeader(): TemplateResult {
        if (this.roomImages && this.roomImages.length > 0) {
            return html`
                <div class="header">
                    ${this.roomImages?.map((url) => html`
                        <img src="/assets/img/rooms/${url}.png" alt="${url}"/>`)}
                </div>
            `;
        }

        return html`${nothing}`;
    }

    private renderContent(): TemplateResult {
        const puzzleElement: Element = document.querySelector("puzzle-game") as Element;
        const shadowRoot: ShadowRoot = puzzleElement.shadowRoot as ShadowRoot;

        const keypadElement: Element = document.querySelector("keypad-component") as Element;
        const keypadShadowRoot: ShadowRoot = keypadElement.shadowRoot as ShadowRoot;

        if (this.contentText?.includes("start-puzzle")) {
            const modal: HTMLElement = shadowRoot.querySelector(".modal") as HTMLElement;
            if (modal) {
                modal.style.display = "flex";
            }
        }

        if (this.contentText?.includes("The riddle: 'Number of seconds in a day without the last two digits'.")) {
            const keypadModal: HTMLElement = keypadShadowRoot.querySelector(".keypadWrap") as HTMLElement;
            if (keypadModal) {
                keypadModal.style.display = "flex";
            }
        }

        if (this.contentText?.includes("It looks like its our house but in a game world")) {
            if (confirm("You are about to play the game on Emily's computer")) {
                this.handleClickRestart();
            }
        }

        return html`
            <div class="content">${this.contentText?.map((text) => html`<p>${text}</p>`)}</div>`;
    }

    private renderFooter(): TemplateResult {
        return html`
            <div class="footer">
                <div class="buttons">
                    <div>
                        ${this.actionButtons?.map(
                            (button) => html`<a
                                class="button ${this.selectedActionButton === button ? "active" : ""}"
                                @click=${(): void => void this.handleClickAction(button)}
                            >${button.label}</a
                            >`
                        )}
                    </div>
                    <div>
                        ${this.selectedActionButton
                            ? this.gameObjectButtons?.map(
                                (button) => html`<a
                                    class="button ${this.selectedGameObjectButtons.has(button)
                                        ? "active"
                                        : ""}"
                                    @click=${(): void => void this.handleClickObject(button)}
                                >${button.name}</a
                                >`
                            )
                            : nothing}
                    </div>
                </div>
                <div class="inventory">
                    <h2>Inventory</h2>
                    ${this.inventoryItemsCount()}
                </div>
            </div>
        `;
    }

    private inventoryItemsCount(): TemplateResult {
        const itemCount: Record<string, number> = {};

        (this.inventoryItems || []).forEach((item) => {
            itemCount[item] = (itemCount[item] || 0) + 1;
        });

        return html`${Object.entries(itemCount)
            .filter(([, count]) => count > 0)
            .reverse()
            .map(([name, count]) => html`
                <div class="inventory-item">${count > 1 ? `${name} (${count})` : name}</div>`)
        }`;
    }
}